<script setup>
console.log('🚀 GM Channel Performance Chart: Initializing...');

import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { Chart, registerables } from 'chart.js';
import GmChartControls from '../filters/GmChartControls.vue';
import GmAnalyticsAPI from 'dashboard/api/gmAnalytics';

Chart.register(...registerables);

const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  title: {
    type: String,
    default: 'Channel Performance'
  },
  height: {
    type: Number,
    default: 300
  },
  channels: {
    type: Array,
    default: () => []
  },
  agents: {
    type: Array,
    default: () => []
  },
  filters: {
    type: Object,
    default: () => ({})
  }
});

// Emits
const emit = defineEmits(['refresh', 'export', 'filters-change']);

const { t } = useI18n();

console.log('📊 GM Channel Performance Chart: Props received:', {
  data: props.data,
  title: props.title
});

// Reactive data
const isLoading = ref(false);
const lastUpdated = ref(null);
const filters = ref({
  dateRange: { from: null, to: null, preset: 'last_30_days' },
  period: { type: 'month', value: null },
  channel: null,
  agent: null
});
const chartInstance = ref(null);
const chartCanvas = ref(null);
const filteredData = ref([]);

// Computed
const chartData = computed(() => {
  const dataToUse = filteredData.value.length > 0 ? filteredData.value : props.data;
  console.log('📊 GM Channel Performance Chart: Chart data:', dataToUse);
  console.log('📊 GM Channel Performance Chart: Chart data length:', dataToUse?.length);
  console.log('📊 GM Channel Performance Chart: Chart data condition:', !dataToUse || dataToUse.length === 0);
  return dataToUse;
});

const chartStats = computed(() => {
  const dataToUse = filteredData.value.length > 0 ? filteredData.value : props.data;
  if (!dataToUse || dataToUse.length === 0) return null;
  
  const totalChannels = dataToUse.length;
  const totalConversations = dataToUse.reduce((sum, channel) => sum + (channel.count || 0), 0);
  const mostActiveChannel = dataToUse.reduce((max, channel) => 
    (channel.count || 0) > (max.count || 0) ? channel : max, dataToUse[0]
  );
  
  return {
    totalChannels,
    totalConversations,
    mostActiveChannel: mostActiveChannel?.name || 'N/A'
  };
});

// Methods
const fetchChartData = async () => {
  console.log('🔄 GM Channel Performance Chart: Fetching chart data with filters:', filters.value);
  try {
    isLoading.value = true;
    
    // Make API call to get filtered channel performance data
    const response = await GmAnalyticsAPI.getChannelPerformance(filters.value);
    console.log('🔍 GM Channel Performance Chart: API response:', response);
    
    if (response.data && response.data.success) {
      // Update the chart data with filtered results
      filteredData.value = response.data.data.channelPerformance || [];
      console.log('✅ GM Channel Performance Chart: Filtered data received:', filteredData.value);
    }
    
    lastUpdated.value = new Date().toISOString();
    console.log('✅ GM Channel Performance Chart: Chart data fetched successfully');
    
    // Update the chart with new data
    await updateChart();
  } catch (error) {
    console.error('❌ GM Channel Performance Chart: Error fetching chart data:', error);
    // Fallback to original data if API fails
    filteredData.value = props.data || [];
    await updateChart();
  } finally {
    isLoading.value = false;
  }
};

const updateChart = async () => {
  console.log('🔄 GM Channel Performance Chart: Updating chart with new data...');
  
  if (!chartCanvas.value) return;
  
  // Destroy existing chart if it exists
  if (chartInstance.value) {
    chartInstance.value.destroy();
    chartInstance.value = null;
  }
  
  const dataToUse = filteredData.value.length > 0 ? filteredData.value : props.data;
  console.log('🔍 GM Channel Performance Chart: dataToUse:', dataToUse);
  
  if (!dataToUse || dataToUse.length === 0) {
    console.log('📊 GM Channel Performance Chart: No data to display');
    return;
  }
  
  const labels = dataToUse.map(item => item.name || 'Unknown');
  const data = dataToUse.map(item => item.count || 0);
  console.log('🔍 GM Channel Performance Chart: labels:', labels);
  console.log('🔍 GM Channel Performance Chart: data:', data);
  
  // Generate colors for each channel
  const colors = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'
  ];
  
  const ctx = chartCanvas.value.getContext('2d');
  
  chartInstance.value = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: colors.slice(0, labels.length),
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 4
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            padding: 20,
            usePointStyle: true,
            font: {
              size: 12
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          borderColor: '#3b82f6',
          borderWidth: 1,
          callbacks: {
            label: function(context) {
              const label = context.label || ''
              const value = context.parsed
              const total = context.dataset.data.reduce((a, b) => a + b, 0)
              const percentage = ((value / total) * 100).toFixed(1)
              return `${label}: ${value} conversations (${percentage}%)`
            }
          }
        }
      }
    }
  });
  
  console.log('✅ GM Channel Performance Chart: Chart updated successfully');
};

const refreshChart = async () => {
  console.log('🔄 GM Channel Performance Chart: Manual refresh requested');
  await fetchChartData();
  emit('refresh', props.filters);
};

const exportChart = () => {
  console.log('📤 GM Channel Performance Chart: Export requested');
  emit('export', props.filters);
};

const onFiltersChange = async (newFilters) => {
  console.log('🔧 GM Channel Performance Chart: Filters changed:', newFilters);
  filters.value = newFilters;
  emit('filters-change', { chartType: 'channel-performance', filters: newFilters });
  await fetchChartData();
};

// Watch for external filter changes
watch(() => props.filters, (newFilters) => {
  if (newFilters) {
    console.log('🔧 GM Channel Performance Chart: External filters changed:', newFilters);
    filters.value = { ...filters.value, ...newFilters };
    updateChart();
  }
}, { deep: true });

// Lifecycle
onMounted(async () => {
  console.log('🎯 GM Channel Performance Chart: Component mounted');
  lastUpdated.value = new Date().toISOString();
  await nextTick();
  updateChart();
});
</script>

<template>
  <div class="gm-channel-performance-chart">
    <!-- Chart Controls -->
    <GmChartControls
      :title="title"
      icon="📊"
      :is-loading="isLoading"
      :last-updated="lastUpdated"
      :show-additional-filters="true"
      :channels="channels"
      :agents="agents"
      v-model="filters"
      @refresh="refreshChart"
      @export="exportChart"
      @filters-change="onFiltersChange"
    />
    
    <!-- Chart Stats -->
    <div v-if="chartStats" class="gm-chart-stats">
      <div class="gm-stat-item">
        <span class="gm-stat-label">Total Channels</span>
        <span class="gm-stat-value">{{ chartStats.totalChannels }}</span>
      </div>
      <div class="gm-stat-item">
        <span class="gm-stat-label">Total Conversations</span>
        <span class="gm-stat-value">{{ chartStats.totalConversations }}</span>
      </div>
      <div class="gm-stat-item">
        <span class="gm-stat-label">Most Active</span>
        <span class="gm-stat-value">{{ chartStats.mostActiveChannel }}</span>
      </div>
    </div>
    
    <!-- Chart Container -->
    <div class="gm-chart-container">
      <div class="gm-chart-placeholder" :style="{ height: height + 'px' }">
        <div v-if="!chartData || chartData.length === 0" class="gm-chart-message">
          <div class="gm-chart-icon-large">📊</div>
          <h3>No Channel Data Available</h3>
          <p>Channel performance data will appear here once available</p>
        </div>
        <div v-else class="gm-chart-canvas-container" :style="{ height: height + 'px' }">
          <canvas ref="chartCanvas"></canvas>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gm-channel-performance-chart {
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  max-height: 500px;
  overflow: hidden;
}

.gm-channel-performance-chart:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.gm-chart-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem 0;
  padding: 1rem 0;
  border-top: 1px solid #f1f5f9;
  border-bottom: 1px solid #f1f5f9;
}

.gm-stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.gm-stat-label {
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
}

.gm-stat-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
}

.gm-chart-container {
  margin-bottom: 1rem;
  height: 300px;
  overflow: hidden;
}

.gm-chart-placeholder {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.gm-chart-message {
  text-align: center;
  padding: 2rem;
  max-width: 400px;
}

.gm-chart-icon-large {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.6;
}

.gm-chart-message h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.gm-chart-message p {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.gm-chart-canvas-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.gm-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #64748b;
}

.gm-loading-spinner {
  width: 2rem;
  height: 2rem;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 0.5rem;
}

.gm-no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #94a3b8;
}

.gm-no-data-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>